import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Popover, Tooltip } from 'antd';
import {
  ArrowUpOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { buildValidationIssues } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/wizardValidationIssues';
import {
  findDisplayedColumn,
  focusGridCell,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/wizardGridNavigation';

import 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/styles/WizardValidationPopover.less';

const POPOVER_Z_INDEX = 1200;

const ICON_TOOLTIP_PROPS = {
  placement: 'top',
  zIndex: POPOVER_Z_INDEX + 100,
  color: '#fff',
  overlayInnerStyle: { color: 'rgba(0, 0, 0, 0.85)' },
};

const HINT_COLORS = {
  error: { background: '#fff1f0', text: '#cf1322' },
  warning: { background: '#fffbe6', text: '#ad6800' },
};

const pluralize = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const focusOnMount = (element) => element?.focus({ preventScroll: true });

const IssueIndicator = ({ warning }) =>
  warning ? (
    <WarningOutlined
      className='wizard-validation-popover__indicator wizard-validation-popover__indicator--warning'
      aria-label='Warning'
    />
  ) : (
    <ExclamationCircleOutlined
      className='wizard-validation-popover__indicator wizard-validation-popover__indicator--error'
      aria-label='Error'
    />
  );

const IssueContent = ({ issue, warning }) => (
  <>
    <IssueIndicator warning={warning} />
    <span className='wizard-validation-popover__text'>
      <span className='wizard-validation-popover__label'>{issue.label}</span>
      <span className='wizard-validation-popover__description'>
        {issue.description}
      </span>
      {issue.detail && (
        <span className='wizard-validation-popover__detail'>{issue.detail}</span>
      )}
    </span>
  </>
);

const IssueList = ({ title, issues, warning, onSelect }) => (
  <section>
    {title && (
      <h4 className='wizard-validation-popover__section-title'>{title}</h4>
    )}
    <ul className='wizard-validation-popover__list'>
      {issues.map((issue) => (
        <li key={issue.key}>
          {issue.column ? (
            <button
              type='button'
              className='wizard-validation-popover__item wizard-validation-popover__item--action'
              onClick={() => onSelect(issue)}
            >
              <IssueContent issue={issue} warning={warning} />
              <Tooltip title='Go to field in grid' {...ICON_TOOLTIP_PROPS}>
                <ArrowUpOutlined
                  className='wizard-validation-popover__navigate'
                  aria-hidden
                />
              </Tooltip>
            </button>
          ) : (
            <div className='wizard-validation-popover__item'>
              <IssueContent issue={issue} warning={warning} />
            </div>
          )}
        </li>
      ))}
    </ul>
  </section>
);

const ValidationIssuesPanel = ({
  errors,
  warnings,
  confirmations,
  onClose,
  onSelect,
}) => {
  const hasErrors = errors.length > 0;
  const groupCount = [errors, warnings, confirmations].filter(
    (issues) => issues.length > 0
  ).length;
  const showSectionTitles = groupCount > 1;

  return (
    <div
      className='wizard-validation-popover__panel'
      role='dialog'
      aria-label='Validation Issues'
      tabIndex={-1}
      ref={focusOnMount}
    >
      <div className='wizard-validation-popover__header'>
        <IssueIndicator warning={!hasErrors} />
        <span className='wizard-validation-popover__title'>
          Validation Issues
        </span>
        <Badge
          count={
            hasErrors ? errors.length : warnings.length + confirmations.length
          }
          color={hasErrors ? undefined : '#faad14'}
        />
        <Button
          type='text'
          size='small'
          icon={<CloseOutlined />}
          aria-label='Close validation issues'
          className='wizard-validation-popover__close'
          onClick={onClose}
        />
      </div>
      <p className='wizard-validation-popover__intro'>
        {hasErrors
          ? 'Complete the following required fields before submitting.'
          : 'Review the following before submitting.'}
      </p>
      <div className='wizard-validation-popover__body'>
        {hasErrors && (
          <IssueList
            title={showSectionTitles ? pluralize(errors.length, 'Error') : null}
            issues={errors}
            onSelect={onSelect}
          />
        )}
        {warnings.length > 0 && (
          <IssueList
            title={
              showSectionTitles ? pluralize(warnings.length, 'Warning') : null
            }
            issues={warnings}
            warning
            onSelect={onSelect}
          />
        )}
        {confirmations.length > 0 && (
          <IssueList
            title={showSectionTitles ? 'Needs Confirmation' : null}
            issues={confirmations}
            warning
            onSelect={onSelect}
          />
        )}
        {(warnings.length > 0 || confirmations.length > 0) && (
          <p className='wizard-validation-popover__note'>
            Click Submit to review and resolve warnings.
          </p>
        )}
      </div>
    </div>
  );
};

const ValidationIssuesPopover = ({ data, api, icon }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  const { errors, warnings, confirmations } = useMemo(
    () => buildValidationIssues(data?.validationErrors),
    [data?.validationErrors]
  );

  const withColumns = (issues) =>
    issues.map((issue) => ({
      ...issue,
      column: findDisplayedColumn(api, issue.field),
    }));
  const errorIssues = withColumns(errors);
  const warningIssues = withColumns(warnings);
  const confirmationIssues = withColumns(confirmations);

  const hasErrors = errors.length > 0;
  const hasIssues =
    hasErrors || warnings.length > 0 || confirmations.length > 0;
  const issueCountLabel = [
    hasErrors && pluralize(errors.length, 'error'),
    warnings.length > 0 && pluralize(warnings.length, 'warning'),
    confirmations.length > 0 &&
      `${pluralize(confirmations.length, 'confirmation')} needed`,
  ]
    .filter(Boolean)
    .join(', ');
  const hintColors = hasErrors ? HINT_COLORS.error : HINT_COLORS.warning;

  const close = useCallback(() => setOpen(false), []);

  const closeAndRestoreFocus = useCallback(() => {
    close();
    triggerRef.current?.focus({ preventScroll: true });
  }, [close]);

  useEffect(() => {
    if (!hasIssues) close();
  }, [hasIssues, close]);

  useEffect(() => {
    if (!open) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closeAndRestoreFocus();
    };

    document.addEventListener('keydown', closeOnEscape);
    api?.addEventListener('bodyScroll', close);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      api?.removeEventListener('bodyScroll', close);
    };
  }, [open, api, close, closeAndRestoreFocus]);

  const handleSelect = (issue) => {
    close();
    setTimeout(() => focusGridCell(api, data.uniqueId, issue.column), 0);
  };

  if (!hasIssues) {
    return (
      <span className='wizard-validation-popover__status-icon'>{icon}</span>
    );
  }

  return (
    <Tooltip
      title={open ? null : `${issueCountLabel}. Click for details`}
      placement='top'
      zIndex={POPOVER_Z_INDEX}
      color={hintColors.background}
      overlayInnerStyle={{ color: hintColors.text }}
    >
      <span className='wizard-validation-popover__status-icon'>
        <Popover
          visible={open}
          onVisibleChange={setOpen}
          trigger='click'
          placement='rightTop'
          zIndex={POPOVER_Z_INDEX}
          overlayClassName='wizard-validation-popover'
          destroyTooltipOnHide
          content={
            <ValidationIssuesPanel
              errors={errorIssues}
              warnings={warningIssues}
              confirmations={confirmationIssues}
              onClose={closeAndRestoreFocus}
              onSelect={handleSelect}
            />
          }
        >
          <Button
            ref={triggerRef}
            type='text'
            className='wizard-validation-popover__trigger'
            aria-label={issueCountLabel}
            aria-haspopup='dialog'
            aria-expanded={open}
            onClick={(event) => event.stopPropagation()}
          >
            {icon}
          </Button>
        </Popover>
      </span>
    </Tooltip>
  );
};

export default ValidationIssuesPopover;
